import { useCallback, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { FieldSpecTable } from '@/components/table/FieldSpecTable/FieldSpecTable';
import {
  addFieldSchemaValue,
  getFieldTableRows,
  updateFieldSchemaValue,
  getTakenNames,
  getCopyableFields,
  deleteFieldSchemaValue,
} from '@/lib/datasource';
import { Datasource_FieldSpec } from '@/types/api';
import {
  FieldSpecSchemaValues,
  TemporaryFieldSchemaValues,
} from '@/zod/schemas/dataSource/Common';
import { FormError } from '@/components/form/form';
import CustomFieldDialog from '@/components/dialog/customField/CustomField';
import { FieldSpecTableRow } from '@/components/table/FieldSpecTable/FieldSpecTableData';

export type DatasourceTemporaryFieldsProps = {
  values: TemporaryFieldSchemaValues;
  prefix: string;
  disabled?: boolean;
  currentTable: 'gold' | 'silver';
  previousTable: 'silver' | 'bronze';
};

export const DatasourceTemporaryFields = ({
  values,
  prefix,
  disabled,
  currentTable,
  previousTable,
}: DatasourceTemporaryFieldsProps) => {
  const { temporaryFields } = values;
  const [customField, setCustomField] = useState<
    FieldSpecSchemaValues | undefined
  >(undefined);
  const rows = useMemo(() => {
    return getFieldTableRows([], temporaryFields);
  }, [temporaryFields]);

  const { setFieldValue } = useFormikContext<TemporaryFieldSchemaValues>();

  const onDeleteField = useCallback(
    (field: FieldSpecSchemaValues) => {
      setFieldValue(
        `${prefix}temporaryFields`,
        deleteFieldSchemaValue(field.name!, temporaryFields),
        true,
      );
    },
    [temporaryFields, setFieldValue, prefix],
  );

  const onEditField = useCallback(
    ({
      custom,
    }: {
      preset?: Datasource_FieldSpec;
      custom?: FieldSpecSchemaValues;
    }) => {
      setCustomField(custom);
    },
    [],
  );

  const addField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        `${prefix}temporaryFields`,
        addFieldSchemaValue(fieldSpec, temporaryFields),
        true,
      );
    },
    [setFieldValue, temporaryFields, prefix],
  );

  const updateField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      setFieldValue(
        `${prefix}temporaryFields`,
        updateFieldSchemaValue(fieldSpec, customField, temporaryFields),
        true,
      );
    },
    [temporaryFields, setFieldValue, prefix, customField],
  );

  const onReorderDown = useCallback(
    (row: FieldSpecTableRow) => {
      const index = temporaryFields.findIndex((q) => q === row.source.custom);
      if (index === -1) {
        return;
      }
      const newFields = [...temporaryFields];
      const temp = newFields[index];
      newFields[index] = newFields[index + 1];
      newFields[index + 1] = temp;
      setFieldValue(`${prefix}temporaryFields`, newFields, true);
    },
    [temporaryFields, setFieldValue, prefix],
  );

  const onReorderUp = useCallback(
    (row: FieldSpecTableRow) => {
      const index = temporaryFields.findIndex((q) => q === row.source.custom);
      if (index === -1) {
        return;
      }
      const newFields = [...temporaryFields];
      const temp = newFields[index];
      newFields[index] = newFields[index - 1];
      newFields[index - 1] = temp;
      setFieldValue(`${prefix}temporaryFields`, newFields, true);
    },
    [temporaryFields, setFieldValue, prefix],
  );

  const takenNames = useMemo(() => getTakenNames(rows), [rows]);
  const copyableFields = useMemo(() => getCopyableFields(rows), [rows]);

  return (
    <>
      <FieldSpecTable
        data={rows}
        onDelete={onDeleteField}
        onEdit={onEditField}
        disabled={disabled}
        onReorderDown={onReorderDown}
        onReorderUp={onReorderUp}
        data-testid={`${prefix}-temporary-fields-table`}
      />
      <FormError name={`${prefix}temporaryFields`} />
      <div>
        <Spacer size="lg" />
        <Button
          leftIcon="radix-icons:plus"
          variant="default"
          className="w-fit"
          onClick={() => setCustomField({})}
          type="button"
        >
          Add a Field
        </Button>
      </div>
      {customField && (
        <CustomFieldDialog
          currentTable={currentTable}
          currentTableFields={copyableFields.filter(
            (name) => name !== customField.name,
          )}
          previousTable={previousTable}
          onSubmit={(fieldSpec: Datasource_FieldSpec) => {
            if (Object.keys(customField).length === 0) {
              addField(fieldSpec);
            } else {
              updateField(fieldSpec);
            }
          }}
          fieldSpec={customField}
          open={true}
          onClose={() => setCustomField(undefined)}
          takenNames={takenNames.filter((name) => name !== customField.name)}
          hideAliasTab
        />
      )}
    </>
  );
};
